import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowRight,
  Car,
  Database,
  Fuel,
  MapPin,
  Plus,
  Settings2,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  FUEL_PRICE_LABELS,
  type DashboardCharts,
  type FuelPricePoint,
  type NetworkOverview,
  type PlatformOverview,
} from "@/lib/analytics";
import type { Car as CarType } from "@/types/database";
import { formatFuelPrice, formatNumber, carTitle } from "@/lib/utils";
import { MakeModelGrid } from "@/components/cars/MakeModelGrid";
import { OptionSetWizard } from "@/components/cars/OptionSetWizard";
import { LazyCarsByMakeChart, LazyFuelCharts } from "@/components/overview/DashboardChartsClient";
import { StationBrandMark } from "@/components/gas-stations/StationBrandMark";
import { SafeImage } from "@/components/ui/SafeImage";
import { Card, FeatureTile, MetricCard, QuickAction, SectionHeader, AnnouncementPill } from "@/components/ui/Card";
import { PageHeader } from "@/components/ui/PageHeader";

type Props = {
  overview: NetworkOverview;
  charts: DashboardCharts;
  userCount: number;
  recentCars: CarType[];
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-wider text-muted">{children}</p>
  );
}

function FuelExtremePanel({
  kind,
  point,
}: {
  kind: "highest" | "lowest";
  point: FuelPricePoint | null;
}) {
  const isHigh = kind === "highest";
  const Icon = isHigh ? TrendingUp : TrendingDown;

  return (
    <Card className="p-4" flat>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={
              isHigh
                ? "flex h-8 w-8 items-center justify-center rounded-full bg-[#fff4e5] text-fuel-high"
                : "flex h-8 w-8 items-center justify-center rounded-full bg-accent-subtle text-fuel-low"
            }
          >
            <Icon className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <div>
            <SectionLabel>
              {isHigh ? "Highest" : "Lowest"} {FUEL_PRICE_LABELS.fuelPrice_87}
            </SectionLabel>
            {point ? (
              <p className="mt-1 text-xl font-extrabold tracking-tight">{formatFuelPrice(point.price)}</p>
            ) : (
              <p className="mt-1 text-sm text-muted">No data</p>
            )}
          </div>
        </div>
        {point ? <StationBrandMark station={point.station} size={28} /> : null}
      </div>
      {point ? (
        <>
          <p className="mt-3 truncate text-sm font-bold">{point.station.name}</p>
          <p className="mt-0.5 flex items-start gap-1 text-xs text-muted">
            <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{point.station.parish || point.station.address}</span>
          </p>
        </>
      ) : null}
    </Card>
  );
}

function FuelPriceRangeBar({ overview }: { overview: NetworkOverview }) {
  const { fuel87, avgFuel87 } = overview;
  if (!fuel87.highest || !fuel87.lowest) return null;

  const min = fuel87.lowest.price;
  const max = fuel87.highest.price;
  const span = max - min || 1;
  const avgPct = ((avgFuel87 - min) / span) * 100;

  return (
    <div className="space-y-3">
      <SectionLabel>E-10 87 price range</SectionLabel>
      <div className="relative pt-1">
        <div className="h-2.5 overflow-hidden rounded-full bg-input">
          <div className="h-full w-full rounded-full bg-gradient-to-r from-[#34c759] via-[#fff200] to-[#ff9500]" />
        </div>
        <div
          className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-foreground shadow-sm"
          style={{ left: `${Math.min(Math.max(avgPct, 3), 97)}%` }}
          aria-hidden
        />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Low</p>
          <p className="mt-0.5 font-extrabold">{formatFuelPrice(min)}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Avg</p>
          <p className="mt-0.5 font-extrabold">{formatFuelPrice(avgFuel87)}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">High</p>
          <p className="mt-0.5 font-extrabold">{formatFuelPrice(max)}</p>
        </div>
      </div>
    </div>
  );
}

function RecentCarsList({ cars }: { cars: CarType[] }) {
  if (cars.length === 0) {
    return <p className="px-4 py-6 text-sm text-muted">No cars on the platform yet.</p>;
  }

  return (
    <ul className="divide-y divide-border">
      {cars.map((car) => (
        <li key={car.carID}>
          <Link
            to={`/cars/${car.carID}`}
            className="list-row"
          >
            <div className="relative h-10 w-12 shrink-0 overflow-hidden rounded-lg bg-input">
              <SafeImage src={car.carImage} alt={carTitle(car)} fill className="object-cover" sizes="48px" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{carTitle(car)}</p>
              <p className="truncate text-xs text-muted">{car.make ?? "Unknown make"}</p>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ViewAllLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1 text-xs font-bold text-accent-link transition-all hover:gap-1.5"
    >
      {label} <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );
}

export function DashboardPageHeader() {
  return (
    <PageHeader
      greetingOnly
      userName="Kevon"
      description="Live snapshot across users, cars, fuel pricing, and platform configuration."
    />
  );
}

export function DashboardAnnouncement() {
  return (
    <AnnouncementPill href="/gas-stations">
      <span className="text-foreground">Fuel network updated</span>
      <span>·</span>
      <span>View latest E-10 87 prices</span>
      <ArrowRight className="h-3.5 w-3.5" />
    </AnnouncementPill>
  );
}

export function DashboardFeatureGrid({
  overview,
  userCount,
}: {
  overview: NetworkOverview;
  userCount: number;
}) {
  return (
    <div className="stagger-children grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      <FeatureTile
        href="/cars"
        label="Cars"
        meta={`${overview.carCount} vehicles`}
        icon={<Car className="h-5 w-5 text-accent-link" />}
        iconClassName="bg-accent-subtle"
      />
      <FeatureTile
        href="/gas-stations"
        label="Fuel network"
        meta={`${overview.gasStationCount} stations`}
        icon={<Fuel className="h-5 w-5 text-fuel-high" />}
        iconClassName="bg-[#fff4e5]"
      />
      <FeatureTile
        href="/cars"
        label="Users"
        meta={`${userCount} registered`}
        icon={<Users className="h-5 w-5 text-[#2563eb]" />}
        iconClassName="bg-[#eff6ff]"
      />
      <FeatureTile
        href="/cars"
        label="Catalogue"
        meta={`${overview.uniqueMakes} makes`}
        icon={<Settings2 className="h-5 w-5 text-[#7c3aed]" />}
        iconClassName="bg-[#f5f3ff]"
      />
      <FeatureTile
        href="/gas-stations"
        label="Avg E-10 87"
        meta={formatFuelPrice(overview.avgFuel87)}
        icon={<TrendingDown className="h-5 w-5 text-fuel-low" />}
        iconClassName="bg-accent-subtle"
      />
      <FeatureTile
        href="/"
        label="Coverage"
        meta={`${overview.stationsWithPricing} priced`}
        icon={<Database className="h-5 w-5 text-muted" />}
        iconClassName="bg-canvas-hover"
      />
    </div>
  );
}

export function DashboardQuickActions() {
  return (
    <div className="space-y-2">
      <QuickAction
        href="/cars"
        icon={<Car className="h-4 w-4" />}
        title="Browse fleet"
        description="View and edit cars owned by platform users"
      />
      <QuickAction
        href="/gas-stations"
        icon={<Fuel className="h-4 w-4" />}
        title="Monitor fuel prices"
        description="Search stations, filter by parish, update E-10 87"
      />
      <QuickAction
        href="/cars"
        icon={<Settings2 className="h-4 w-4" />}
        title="Manage catalogue"
        description="Make / model option sets for the add-car flow"
      />
    </div>
  );
}

export function DashboardTopKpis({
  overview,
  userCount,
}: {
  overview: NetworkOverview;
  userCount: number;
}) {
  const coveragePct = Math.round(
    (overview.stationsWithPricing / Math.max(overview.gasStationCount, 1)) * 100
  );

  return (
    <div className="stagger-children grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        label="Platform cars"
        value={formatNumber(overview.carCount)}
        detail={`${overview.uniqueMakes} makes listed`}
      />
      <MetricCard
        label="Registered users"
        value={formatNumber(userCount)}
        detail={`${overview.uniqueOwners} owners with cars`}
      />
      <MetricCard
        label="Fuel stations"
        value={formatNumber(overview.gasStationCount)}
        detail={`${coveragePct}% reporting E-10 87`}
      />
      <MetricCard
        label="Avg E-10 87"
        value={formatFuelPrice(overview.avgFuel87)}
        detail={`${formatFuelPrice(overview.spread)} spread`}
        trend="neutral"
      />
    </div>
  );
}

export function DashboardHomeFeed({
  recentCars,
}: {
  recentCars: CarType[];
}) {
  return (
    <Card className="overflow-hidden" flat>
      <div className="border-b border-border px-4 py-3">
        <SectionHeader
          title="Latest from fleet"
          description="Recently added vehicles on the platform."
          action={<ViewAllLink href="/cars" label="View all" />}
        />
      </div>
      <RecentCarsList cars={recentCars} />
    </Card>
  );
}

export function UsersCarsColumn({
  charts,
}: {
  overview: NetworkOverview;
  charts: DashboardCharts;
}) {
  return (
    <Card className="overflow-hidden p-5 sm:p-6">
      <SectionHeader
        title="Cars breakdown"
        description="Cars grouped by manufacturer on the platform."
      />
      <div className="mt-5">
        <LazyCarsByMakeChart charts={charts} />
      </div>
    </Card>
  );
}

export function FuelColumn({
  overview,
  charts,
}: {
  overview: NetworkOverview;
  charts: DashboardCharts;
}) {
  const { fuel87 } = overview;

  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <SectionHeader
          title="Fuel & prices"
          description="E-10 87 across the Jamaican fuel network."
          action={<ViewAllLink href="/gas-stations" label="View network" />}
        />
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <MetricCard label="Stations" value={formatNumber(overview.gasStationCount)} className="p-4" />
          <MetricCard label="Avg E-10 87" value={formatFuelPrice(overview.avgFuel87)} className="p-4" />
          <MetricCard label="Spread" value={formatFuelPrice(overview.spread)} className="p-4" />
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <div className="grid gap-3 lg:grid-cols-2">
          <FuelExtremePanel kind="lowest" point={fuel87.lowest} />
          <FuelExtremePanel kind="highest" point={fuel87.highest} />
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <FuelPriceRangeBar overview={overview} />
      </Card>

      <Card className="overflow-hidden p-5 sm:p-6">
        <LazyFuelCharts charts={charts} />
      </Card>
    </div>
  );
}

export function PlatformColumn({ platform }: { platform: PlatformOverview }) {
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <>
      <Card className="overflow-hidden p-5 sm:p-6">
        <SectionHeader
          title="Platform catalogue"
          description="Reference data and vehicle option sets."
          action={
            <button
              type="button"
              aria-label="Create option set"
              onClick={() => setWizardOpen(true)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-white opacity-100 transition hover:bg-[var(--color-accent-hover)] focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <Plus className="h-5 w-5" />
            </button>
          }
        />
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <MetricCard
          label="Option sets"
          value={formatNumber(platform.optionSetCount)}
          className="p-4"
          info={{
            title: "What are option sets?",
            description:
              "Option sets are reference records in the CarOptionSet catalogue — one per vehicle make — listing the models and trims users can choose when adding a car in the Shift app. This count is the number of makes configured in the catalogue, not live cars on the platform.",
          }}
        />
        <MetricCard
          label="Catalogue models"
          value={formatNumber(platform.totalModels)}
          className="p-4"
          info={{
            title: "What are catalogue models?",
            description:
              "This is the total number of vehicle models listed across all option sets in the CarOptionSet catalogue. Each make can include many models users can pick when adding a car in Shift — this count reflects configured catalogue entries, not cars currently on the platform.",
          }}
        />
      </div>

      <div className="my-6 border-t border-border/40" />

      <div className="flex items-center justify-between gap-4">
        <SectionLabel>Top makes by models</SectionLabel>
        <ViewAllLink href="/option-sets" label="View all" />
      </div>
      <div className="mt-4">
        <MakeModelGrid platform={platform} />
      </div>
    </Card>

      <OptionSetWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
}

export function DashboardAnalytics({
  overview,
  charts,
  userCount,
  recentCars,
}: Props) {
  return (
    <div className="space-y-10">
      <DashboardAnnouncement />
      <DashboardPageHeader />
      <DashboardFeatureGrid overview={overview} userCount={userCount} />

      <div className="grid items-start gap-8 lg:grid-cols-2">
        <DashboardHomeFeed recentCars={recentCars} />
        <div>
          <h2 className="text-[0.9375rem] font-semibold text-foreground">Quick actions</h2>
          <p className="mt-1 mb-3 text-sm text-muted">Jump into common platform workflows.</p>
          <DashboardQuickActions />
        </div>
      </div>

      <div className="space-y-4">
        <UsersCarsColumn overview={overview} charts={charts} />
        <FuelColumn overview={overview} charts={charts} />
      </div>
    </div>
  );
}
